"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import CommonCalendar from "@/components/utils/calendar/CommonCalendar";
import type { CreateWaveDatesConfig } from "@/types/waves.types";
import TooltipIconButton from "@/components/common/TooltipIconButton";
import { Time } from "@/helpers/time";
import { getEarliestApproveWaveEndTimestamp } from "./approveWaveDates.helpers";
interface CreateWaveDatesApproveStartProps {
  readonly dates: CreateWaveDatesConfig;
  readonly setDates: (dates: CreateWaveDatesConfig) => void;
}
const formatShortDate = (timestamp: number) => new Date(timestamp).toLocaleString("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric"
});
export default function CreateWaveDatesApproveStart(t0) {
  const $ = _c(21);
  const {
    dates,
    setDates
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Time.currentMillis();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const minStartTimestamp = t1;
  let t2;
  if ($[1] !== dates.submissionStartDate) {
    t2 = formatShortDate(dates.submissionStartDate);
    $[1] = dates.submissionStartDate;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const startDateFormatted = t2;
  let t3;
  if ($[3] !== dates || $[4] !== setDates) {
    t3 = timestamp => {
      const adjustedTimestamp = Math.max(timestamp, Time.currentMillis());
      const earliestEndTimestamp = getEarliestApproveWaveEndTimestamp(adjustedTimestamp);
      const selectedEndDate = dates.endDate !== null && Number.isFinite(dates.endDate) ? dates.endDate : null;
      const nextEndDate = selectedEndDate !== null && selectedEndDate < earliestEndTimestamp ? earliestEndTimestamp : selectedEndDate;
      setDates({
        ...dates,
        submissionStartDate: adjustedTimestamp,
        votingStartDate: adjustedTimestamp,
        endDate: nextEndDate
      });
    };
    $[3] = dates;
    $[4] = setDates;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleStartDateChange = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-flex tw-items-center tw-gap-x-2"><h3 className="tw-mb-0 tw-text-base tw-font-semibold tw-text-iron-300">Wave Start</h3><TooltipIconButton icon={faInfoCircle} tooltipText="Choose when the approve wave opens. Approvals and submissions start at the same moment." tooltipPosition="bottom" tooltipWidth="tw-w-80" /></div>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FontAwesomeIcon icon={faCalendarDays} className="tw-mr-2 tw-size-4 tw-text-primary-400" />;
    t6 = <p className="tw-mb-0 tw-text-xs tw-text-iron-300/70">Wave Starts</p>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== startDateFormatted) {
    t7 = <div className="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-start sm:tw-justify-between">{t4}<div className="tw-flex tw-items-center tw-rounded-lg tw-bg-iron-700/40 tw-px-3 tw-py-2 tw-shadow-md tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]">{t5}<div>{t6}<p className="tw-mb-0 tw-text-sm tw-font-medium tw-text-iron-50">{startDateFormatted}</p></div></div></div>;
    $[9] = startDateFormatted;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="tw-mb-0 tw-text-lg tw-font-semibold tw-text-iron-50 sm:tw-text-xl">Wave Starts</p>;
    t9 = <p className="tw-mt-1 tw-text-xs tw-text-iron-400">This is when the approve wave opens for submissions and approvals.</p>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = new Date().getMonth();
    t11 = new Date().getFullYear();
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  let t12;
  if ($[15] !== dates.submissionStartDate || $[16] !== handleStartDateChange) {
    t12 = <div className="tw-mt-5 tw-grid tw-grid-cols-1 tw-gap-x-10 tw-gap-y-8"><div className="tw-col-span-1">{t8}{t9}<div className="tw-mt-4 tw-w-full tw-max-w-md"><CommonCalendar initialMonth={t10} initialYear={t11} selectedTimestamp={dates.submissionStartDate} minTimestamp={minStartTimestamp} maxTimestamp={null} setSelectedTimestamp={handleStartDateChange} /></div></div></div>;
    $[15] = dates.submissionStartDate;
    $[16] = handleStartDateChange;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t12 || $[19] !== t7) {
    t13 = <section className="tw-rounded-xl tw-bg-iron-900 tw-px-5 tw-pb-5 tw-pt-5 tw-shadow-sm tw-ring-1 tw-ring-iron-700/50">{t7}{t12}</section>;
    $[18] = t12;
    $[19] = t7;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
}